import { useCategoryFilterOverlayProps } from "hooks/useCategoryFilterOverlayProps";
import { useProductCardState } from "hooks/useProductCardState";
import { useRequestProductListVariants } from "hooks/useRequestProductListVariants";
import { useSeoUrlHelpers } from "hooks/useSeoUrlHelpers";
import PropTypes from "prop-types";
import React, { Suspense, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { TYPE_SECONDARY } from "Component/Button/Button.config";
import { S_KEY } from "Component/Icon/Icon.config";
import Link from "Component/Link";
import PRODUCT_TYPE from "Component/Product/Product.config";
import ProductCardImage from "Component/ProductCardImage";
import ProductOptions from "Component/ProductOptions/ProductOptions.component";
import {
    COLOR_DISPLAY_GROUP,
    SIZE_DISPLAY_GROUP
} from "Component/ProductOptions/ProductOptions.config";
import ProductPrice from "Component/ProductPrice";
/** @namespace Bodypwa/Component/ProductCard/Component/ProductCard */
import SkeletonComponent from "Component/Skeleton/Skeleton.component";
import {
    PRODUCT_WISHLIST_BUTTON,
    SKELETON_PLP_CARD_DETAILS,
    SKELETON_PLP_CARD_DETAILS_MOBILE
} from "Route/CategoryPage/CategoryPageSkeleton.config";
import { event } from "Store/GoogleTagManager/GoogleTagManager.action";
import { ProductType } from "Type/ProductList.type";
import BrowserDatabase from "Util/BrowserDatabase/BrowserDatabase";
import history from "Util/History";
import { lazyComponentLoader } from "Util/lazyComponentLoader";
import { getMagentoProduct } from "Util/Product/MagentoProduct";
import { STOCK_TYPE } from "Util/Product/Stock.config";
import { appendWithStoreCode, getQueryParam } from "Util/Url";

export const ProductWishlistButton = lazyComponentLoader(
    () =>
        import(
            /* webpackMode: "lazy", webpackChunkName: "menu" */
            "Component/ProductWishlistButton"
        ),
    2
);

const AddToCart = lazyComponentLoader(
    () =>
        import(
            /* webpackMode: "lazy", webpackChunkName: "product-additional" */
            "Component/AddToCart"
        ),
    2
);

/** @namespace Bodypwa/Component/ProductCard/Component/ProductCard */
export function ProductCard(props) {
    const {
        state: { selectedValue = null } = {},
        updateState,
        product,
        product: {
            configurable_options,
            sku,
            page,
            variants,
            stock_status,
            url_rewrites = []
        },
        adjustedPrice = undefined,
        defaultEnteredOptions = [],
        defaultSelectedOptions = [],
        defaultQuantity = 1,
        isLoading = false,
        imageLinkClass = "",
        imageClass = "",
        isSwiper = false,
        gtmEvents = { clickEvent: "", scrollEvent: "" },
        isTeaseSwipe = false,
        swatchClass = "",
        detailsClass = "",
        addToCartButton = 0,
        defaultPlacement = null
    } = props;

    const {
        filters,
        categoryId
        // TODO: cahnge check/definition for filers
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = isSwiper ? { filters: {} } : useCategoryFilterOverlayProps();
    const location = useLocation();
    const isSmallFonts = useSelector(
        (state) => state.ConfigReducer?.product_slider_display_small_fonts
    );

    const placement = useSelector(
        (state) =>
            state.CategoryReducer?.category.placement_suggestion ||
            defaultPlacement ||
            null
    );

    const dispatch = useDispatch();
    const requestProductListVariants = useRequestProductListVariants();
    const { selectedCustomFiltersValues } = useSeoUrlHelpers();
    const gtmEvent = useCallback(
        (eventName = "", customData) => dispatch(event(eventName, customData)),
        [dispatch]
    );
    const isWishlistEnabled = useSelector(
        (state) => state.ConfigReducer.wishlist_general_active
    );
    const baseLinkUrl = useSelector(
        (state) => state.ConfigReducer.base_link_url || ""
    );
    const categoryUrlSuffix = useSelector(
        (state) => state.ConfigReducer.category_url_suffix
    );
    const productUsesCategories = useSelector(
        (state) => state.ConfigReducer.product_use_categories || false
    );
    const isMobile = useSelector(
        (state) => state.ConfigReducer.device.isMobile
    );

    const productCardState = useProductCardState({
        product,
        adjustedPrice,
        defaultEnteredOptions,
        defaultSelectedOptions,
        defaultQuantity
    });

    const { productName, productPrice } = productCardState;

    const selectBasedOnAttributes = useCallback(
        (attrCode, attrValue) => {
            const selectedFilters = Object.keys(filters)
                .filter(
                    (key) =>
                        selectedCustomFiltersValues[key] &&
                        filters[key].attribute_display_group ===
                            COLOR_DISPLAY_GROUP
                )
                .reduce((acc, key) => {
                    acc[key] = filters[key];
                    return acc;
                }, {});

            const matchingVariant = variants.find((variant) =>
                Object.keys(selectedFilters).some((filterKey) => {
                    const filterOptions =
                        selectedFilters[filterKey]?.attribute_options || {};
                    const selectedValues =
                        selectedCustomFiltersValues[filterKey] || [];

                    return selectedValues.some(
                        (value) =>
                            (filterOptions[value]?.grouped_option_ids?.includes(
                                variant[filterKey]
                            ) ||
                                String(variant[filterKey]) === value) &&
                            String(variant[attrCode]) === String(attrValue)
                    );
                })
            );

            return matchingVariant || null;
        },
        [filters, selectedCustomFiltersValues, variants]
    );

    // TODO MOVE THIS TO HOOK OR UTIL IF WWE WILL USE IN PDP
    const getSelectOption = useCallback(
        (attribute_code, attribute_value) => {
            const selSet = new Set(
                selectedCustomFiltersValues[attribute_code] || []
            );
            const opts = Object.values(
                filters[attribute_code]?.attribute_options || {}
            );

            // find *all* matching options for this attribute value
            const matchingOpts = opts.filter(
                (o) =>
                    o.grouped_option_ids
                        ?.map(String)
                        .includes(String(attribute_value)) ||
                    String(o.value_string) === String(attribute_value)
            );

            // collect all grouped IDs + value_strings from those options
            const groupedIds = matchingOpts
                .flatMap((o) => o.grouped_option_ids || [])
                .map(String);
            const valueStrings = matchingOpts
                .map((o) => o.value_string)
                .filter(Boolean);

            // variants that match any of those grouped IDs or value strings
            const hasVariant =
                variants?.filter(
                    (variant) =>
                        groupedIds.includes(String(variant[attribute_code])) ||
                        valueStrings.includes(String(variant[attribute_code]))
                ) || [];

            // if no option matched but we still have selected custom filter values
            if (
                matchingOpts.length === 0 &&
                selectedCustomFiltersValues[attribute_code]
            ) {
                return (
                    hasVariant.length > 0 &&
                    selectedCustomFiltersValues[attribute_code].includes(
                        String(attribute_value)
                    ) &&
                    !selectedValue
                );
            }

            // check if *any* of the valueStrings or groupedIds is in the selected set
            const anyValueStringSelected = valueStrings.some((v) =>
                selSet.has(v)
            );
            const anyGroupedIdSelected = groupedIds.some((id) =>
                selSet.has(id)
            );

            if (
                (!anyValueStringSelected || !anyGroupedIdSelected) &&
                hasVariant.length > 0
            ) {
                return Boolean(
                    selectBasedOnAttributes(attribute_code, attribute_value)
                );
            }

            return Boolean(
                hasVariant.length > 0 &&
                (anyValueStringSelected || anyGroupedIdSelected) &&
                !selectedValue
            );
        },
        [
            selectedCustomFiltersValues,
            selectBasedOnAttributes,
            filters,
            selectedValue,
            variants
        ]
    );

    // TODO MOVE THIS TO HOOK OR UTIL IF WWE WILL USE IN PDP
    const selectedVariant = useMemo(() => {
        if (!variants?.length) {
            return null;
        }

        const optionsArray = Array.isArray(configurable_options)
            ? configurable_options
            : Object.values(configurable_options);

        const mainConfigObj = optionsArray.find(
            (item) => item.attribute_display_group === COLOR_DISPLAY_GROUP
        );
        const attribute_code = mainConfigObj?.attribute_code;
        if (!attribute_code) {
            return null;
        }

        if (selectedValue) {
            const directMatch = variants.find(
                (v) => v[attribute_code] === +selectedValue
            );

            if (directMatch) {
                return directMatch;
            }
        }

        const selectedFromFilters = Array.from(variants)
            .reverse()
            .find((variant) => {
                const value = variant?.[attribute_code];
                return getSelectOption(attribute_code, `${value}`, variant);
            });

        return selectedFromFilters || null;
    }, [selectedValue, variants, configurable_options, getSelectOption]);

    const selectedImage = selectedVariant?.small_image?.url;
    const fallbackImage = product?.small_image?.url;

    const selectedImagePath = selectedImage || fallbackImage;

    const magentoProduct = useMemo(
        () => getMagentoProduct({ product }),
        [product]
    );

    const { name, alt_image, url, small_image } = product;

    const brandValue = product?.attributes?.brand_name?.attribute_value;
    const brandOptions = product?.attributes?.brand_name?.attribute_options;
    const brandLabel = brandOptions?.[brandValue]?.label ?? null;

    const brandElement = useMemo(
        () =>
            brandLabel ? (
                <div className="text-p10 uppercase text-bmText-muted mt-3">
                    { brandLabel }
                </div>
            ) : null,
        [brandLabel]
    );

    const getLinkTo = useCallback(() => {
        const { pathname: storePrefix } = new URL(
            baseLinkUrl || window.location.origin
        );
        const {
            location: { pathname, search: catSearch }
        } = history;

        if (!url) {
            return { pathname: "/" };
        }

        const { state } = history?.location || {};
        const { category = {} } = state || {};
        const categoryUrlPart = pathname
            .replace(storePrefix, "")
            .replace(categoryUrlSuffix, "");
        const productUrl = `${categoryUrlPart}/${url.replace(storePrefix, "")}`;

        const rewriteUrl =
            url_rewrites.find(({ url: u }) => u.includes(productUrl)) || {};

        const rewriteUrlPath = productUsesCategories
            ? (rewriteUrl.url && appendWithStoreCode(rewriteUrl.url)) || url
            : url;

        const optionsArray = Array.isArray(configurable_options)
            ? configurable_options
            : Object.values(configurable_options || {});

        const colorConfig = optionsArray.find(
            (item) => item.attribute_display_group === COLOR_DISPLAY_GROUP
        );

        const attribute_code = colorConfig?.attribute_code;

        let altSelectedValue = null;
        if (attribute_code) {
            if (selectedValue !== null) {
                altSelectedValue = String(selectedValue);
            } else if (
                selectedVariant &&
                selectedVariant[attribute_code] !== null
            ) {
                altSelectedValue = String(selectedVariant[attribute_code]);
            }
        }

        // eslint-disable-next-line no-unused-vars
        const { small_image, ...rest } = product;

        const searchParams = new URLSearchParams();
        if (attribute_code && altSelectedValue) {
            searchParams.append(attribute_code, altSelectedValue);
        }
        const search = searchParams.toString()
            ? `?${searchParams.toString()}`
            : "";

        const fiterSearchParam = getQueryParam("placement", location);

        const hashParams = new URLSearchParams();
        if (placement) {
            hashParams.set(
                "placement",
                fiterSearchParam || placement
            );
        }

        // Logic for size preselection hash
        Object.entries(filters || {})
            .filter(
                ([, filterData]) =>
                    filterData.attribute_display_group === SIZE_DISPLAY_GROUP
            )
            .forEach(([attrCode]) => {
                const selectedFilterValue =
                    selectedCustomFiltersValues[attrCode]?.[0];

                if (!selectedFilterValue) {
                    return;
                }
                const resolvedSizeId = selectedFilterValue;

                hashParams.set(attrCode, resolvedSizeId);
            });

        const hashString = hashParams.toString()
            ? `#${hashParams.toString()}`
            : "";

        return {
            pathname: rewriteUrlPath,
            state: {
                product: rest,
                prevCategoryId: category,
                search: catSearch || ""
            },
            search,
            hash: hashString
        };
    }, [
        baseLinkUrl,
        categoryUrlSuffix,
        configurable_options,
        placement,
        location,
        product,
        productUsesCategories,
        selectedValue,
        selectedVariant,
        url,
        url_rewrites,
        filters,
        selectedCustomFiltersValues
    ]);

    const handleOptionClick = useCallback(
        (attribute) => {
            const value = attribute?.attribute_value;

            if (value === selectedValue) {
                updateState({ selectedValue: null });
                return;
            }
            updateState({ selectedValue: value });

            requestProductListVariants(sku, page);
        },
        [selectedValue, sku, page, requestProductListVariants, updateState]
    );

    const wishlistButton = useMemo(() => {
        if (!isWishlistEnabled || !magentoProduct?.length) {
            return null;
        }

        return (
            <div className="z-20 right-0 top-0  absolute">
                <Suspense
                    fallback={ <SkeletonComponent items={ PRODUCT_WISHLIST_BUTTON } /> }
                >
                    <ProductWishlistButton
                        magentoProduct={ magentoProduct }
                        iconSize={ S_KEY }
                    />
                </Suspense>
            </div>
        );
    }, [isWishlistEnabled, magentoProduct]);

    const renderPrice = useMemo(() => {
        if (stock_status === STOCK_TYPE.IN_STOCK) {
            return (
                <ProductPrice
                    isSmallFonts={ isSmallFonts && isSwiper }
                    price={ productPrice }
                    className="flex items-baseline gap-2 text-p16 mt-1"
                />
            );
        }

        return (
            <p className="text-p14 flex leading-[20px]! text-bmText-muted! mb-0 mt-1! pl-0!">
                { __('Sold out') }
            </p>
        );
    }, [isSmallFonts, isSwiper, productPrice, stock_status]);

    const onProductClick = useCallback(() => {
        const { clickEvent } = gtmEvents || {};

        if (!clickEvent) {
            return;
        }
        const originalScrollY = document.documentElement.scrollTop;
        const originalPageHeight = document.documentElement.scrollHeight;

        if (clickEvent) {
            gtmEvent(clickEvent, {
                ecommerce: {
                    pageType: "Product",
                    eventType: "Upsell",
                    detail: {
                        products: [
                            {
                                id: sku
                            }
                        ]
                    }
                }
            });
        }
        BrowserDatabase.setItem(
            { originalScrollY, originalPageHeight, categoryId },
            "scroll-position"
        );
    }, [gtmEvents, gtmEvent, categoryId, sku]);

    const goToProductPage = useCallback(() => {
        const linkTo = getLinkTo();
        history.push(linkTo);
    }, [getLinkTo]);

    const renderAddToCartButton = useCallback(() => {
        if (
            stock_status !== STOCK_TYPE.IN_STOCK ||
            !isSwiper ||
            !addToCartButton
        ) {
            return null;
        }

        const quantity = defaultQuantity || 1;

        return (
            <div className="mt-3 quick-add-to-cart">
                <AddToCart
                    inStock={ stock_status === STOCK_TYPE.IN_STOCK }
                    product={ product }
                    designType={ TYPE_SECONDARY }
                    size="small"
                    addToCart={ PRODUCT_TYPE.simple !== product?.type_id &&
                        goToProductPage }
                    isRedirect={ PRODUCT_TYPE.simple !== product?.type_id }
                    selectedProduct={ product }
                    quantity={ quantity }
                />
            </div>
        );
    }, [product, stock_status, defaultQuantity, goToProductPage, isSwiper]);

    const renderDetails = () => {
        if (!productName) {
            return (
                <SkeletonComponent
                    items={ isMobile
                            ? SKELETON_PLP_CARD_DETAILS_MOBILE
                            : SKELETON_PLP_CARD_DETAILS }
                />
            );
        }

        return (
            <div className={ `flex flex-col ${detailsClass}` }>
                <div className="min-h-[14px]">{ brandElement }</div>
                <Link
                    to={ getLinkTo() }
                    className="block hover:underline font-medium
                        text-bmText-default text-p14 line-clamp-1 text-ellipsis"
                    onClick={ onProductClick }
                >
                    { productName }
                </Link>
                { renderPrice }
                <ProductOptions
                    url={ getLinkTo() }
                    swatchClass={ swatchClass }
                    isSwiper={ isSwiper }
                    configurable_options={ configurable_options }
                    product={ product }
                    getSelectOption={ getSelectOption }
                    selectedOptionValue={ selectedValue }
                    handleOptionClick={ handleOptionClick }
                    isReady
                />
                { renderAddToCartButton() }
            </div>
        );
    };

    const renderContent = () => (
        <>
            <ProductCardImage
                imageLinkClass={ imageLinkClass }
                imageClass={ imageClass }
                url={ getLinkTo() }
                name={ name }
                selectedImagePath={ selectedImagePath }
                altImage={ alt_image }
                baseSmallImage={ small_image }
                wishlistButton={ wishlistButton }
                onClick={ onProductClick }
                categoryId={ categoryId }
                isVariantSelected={ Boolean(selectedVariant) }
                isTeaseSwipe={ isTeaseSwipe }
                isSwiper={ isSwiper }
                product={ product }
            />
            { renderDetails() }
        </>
    );

    if (isLoading || !product) {
        return null;
    }

    return (
        <div
            className={ `relative flex flex-col transition-all duration-200 w-full h-full
            ${isSwiper ? "swiper-slide" : ""}` }
        >
            { renderContent() }
        </div>
    );
}

ProductCard.propTypes = {
    product: ProductType.isRequired,
    adjustedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    defaultEnteredOptions: PropTypes.arrayOf(PropTypes.object),
    defaultSelectedOptions: PropTypes.arrayOf(PropTypes.string),
    defaultQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    isLoading: PropTypes.bool,
    imageLinkClass: PropTypes.string,
    isSwiper: PropTypes.bool,
    state: PropTypes.shape({
        selectedValue: PropTypes.string
    }),
    updateState: PropTypes.func.isRequired,
    gtmEvents: PropTypes.shape({
        clickEvent: PropTypes.string,
        scrollEvent: PropTypes.string
    }),
    imageClass: PropTypes.string,
    isTeaseSwipe: PropTypes.bool,
    swatchClass: PropTypes.string,
    detailsClass: PropTypes.string,
    addToCartButton: PropTypes.bool
};

/** @namespace Bodypwa/Component/ProductCard/Component/areEqual */
const areEqual = (prevProps, nextProps) => {
    const {
        product: prevProduct,
        state: prevState,
        product: { variants: prevVariants, small_image: prevSmallImage }
    } = prevProps;
    const {
        product: nextProduct,
        state: nextState,
        product: { variants: nextVariants, small_image: nextSmallImage }
    } = nextProps;

    return (
        prevProduct?.price_range?.maximum_price?.final_price ===
            nextProduct?.price_range?.maximum_price?.final_price &&
        prevProduct?.sku === nextProduct?.sku &&
        prevState?.selectedValue === nextState?.selectedValue &&
        prevVariants?.length === nextVariants?.length &&
        prevSmallImage?.path === nextSmallImage?.path
    );
};

export default React.memo(ProductCard, areEqual);
